import util from '../lib/util';
import newHRTimestamp from '../lib/picos/timestamp';
import type {uuid, SchemaField, EntitySchema} from "../@types/schema";
import Base from "./base";
import {genUUID} from "../lib/picos/uuids";
import type {EntityType} from "../@types/entity";
function getCurrentUserId() { return sessionStorage.getItem('userId'); }
function getCurrentFirmId() { return sessionStorage.getItem('firmId'); }
export const Method = {
    Simple: "S",
    Percentage: "P"
};
export type MethodType = typeof Method[keyof typeof Method];
class Discount extends Base implements EntityType {
    discountId: uuid;
    method: MethodType;
    simple?: number | null;
    percentage?: number | null;
    applyToLabor: boolean;
    applyToExpenses: boolean;
    total: number;
    reason: uuid;
    description?: string | null;
    amount?: number | null;
    laborAmount?: number | null;
    expenseAmount?: number | null;
    constructor(doc: typeof Discount | any) {
        super(doc);
        if (!doc) doc = {
            firmId: getCurrentFirmId(),
            addedBy: getCurrentUserId(),
            addedOn: newHRTimestamp(),
            status: "C",
            entityId: null,
            entityType: null,
            discountId: genUUID(),
            method: null,
            total: null,
            reason: null,
        };
        // @ts-ignore
        this.entityId = doc._entityId || doc.entityId || doc.entityid || '00000000-0000-0000-0000-000000000000';
        // @ts-ignore
        this.entityType = doc._entityType || doc.entityType || doc.entitytype || "discount";
        // @ts-ignore
        this.discountId = doc._discountId || doc.discountId || doc.discountid || genUUID();
        // @ts-ignore
        this.method = util.trimRight(doc._method) || util.trimRight(doc.method) || null;
        // @ts-ignore
        this.simple = util.coalesce(util.parseFloat(doc._simple), util.parseFloat(doc.simple), util.parseFloat(doc.simple), null);
        // @ts-ignore
        this.percentage = util.coalesce(util.parseFloat(doc._percentage), util.parseFloat(doc.percentage), util.parseFloat(doc.percentage), null);
        // @ts-ignore
        this.applyToLabor = util.defaultBoolean([doc.applyToLabor, doc._applyToLabor, doc.applytolabor, false]);
        // @ts-ignore
        this.applyToExpenses = util.defaultBoolean([doc.applyToExpenses, doc._applyToExpenses, doc.applytoexpenses, false]);
        // @ts-ignore
        this.total = util.coalesce(util.parseFloat(doc._total), util.parseFloat(doc.total), util.parseFloat(doc.total), null);
        // @ts-ignore
        this.reason = doc._reason || doc.reason || null;
        // @ts-ignore
        this.description = doc._description || doc.description || null;
        // @ts-ignore
        this.amount = util.coalesce(util.parseFloat(doc._amount), util.parseFloat(doc.amount), util.parseFloat(doc.amount), null);
        // @ts-ignore
        this.laborAmount = util.coalesce(util.parseFloat(doc._laborAmount), util.parseFloat(doc.laborAmount), util.parseFloat(doc.laboramount), null);
        // @ts-ignore
        this.expenseAmount = util.coalesce(util.parseFloat(doc._expenseAmount), util.parseFloat(doc.expenseAmount), util.parseFloat(doc.expenseamount), null);
    }
    get id(): uuid { return this.discountId; }
    static METHOD_VALUES = ["S", "P"];
    static override getPkFields(): SchemaField[] { return [{"type":"uuid","required":true,"pk":true,"default":"__UUID__","comment":"Unique id for this discount"},{"type":"uuid","required":true,"pk":true,"base":true,"default":"__FIRM__","comment":"Associated firm"}]; }
    toLowerCase() { return this.discountId; }
    static override getNonRefArrayFields(): string[] { return []; }
    static override getDefaultEntries(): Discount[] {
        return [];
    }
    static override getSchema(): EntitySchema {
        return Base.withBaseFields({
    entity: 'discount',
    dataArea: 'Billing',
    attachedTo: [
        {parents: ['invoice'], via: 'entity', onDelete: 'delete', onReplace: 'leave'},
        {parents: ['matter'], via: 'entity', onDelete: 'delete', onReplace: 'leave'},
    ],
    table: 'DISCOUNT',
    comment: 'A discount applied to a client invoice, recorded as either a flat dollar amount or a percentage of the invoice total. Percentage discounts can be applied selectively to labor charges, expense charges, or both. Each discount carries a required reason (e.g. Professional Courtesy, Court mandated, Paid in full, Cash) and an optional description that appears on the printed invoice.',
    fields: {
        discountId: {
            type: 'uuid',
            required: true,
            pk: true,
            default: '__UUID__',
            comment: 'Unique id for this discount'
        },

        /**
         * Either [s]imple or [p]ercentage.
         */
        method: {
            type: 'char',
            values: ['S','P'],
            valueNames: ['Simple', 'Percentage'],
            required: true,
            comment: 'Simple or percentage'
        },

        /**
         * If the discount is a simple dollar number, enter it here
         */
        simple: {
            type: 'money', comment: 'Simple discount, in $'
        },

        /**
         * If the discount is a percentage, enter it here (8 = 8%)
         */
        percentage: {
            type: 'float', comment: 'Discount (10 = 10%)'
        },

        /**
         * If using a percentage, does the discount apply to labor?
         */
        applyToLabor: {
            type: 'boolean', default: false, comment: 'Should this discount be applied to expenses?'
        },

        /**
         * If using a percentage, does the discount apply to expenses?
         */
        applyToExpenses: {
            type: 'boolean', default: false, comment: 'Should this discount be applied to labor?'
        },

        /**
         * Applying all formulas, what is the total discount?
         */
        total: {
            type: 'money', required: true, comment: 'What is the total discount?'
        },

        /**
         * A lookup for a reason, such as:
         * - Professional Courtesy
         * - Court-Mandated
         * - Cash
         * - Pay-in-full
         */
        reason: {
            type: 'uuid',
            entityType: 'lookup',
            lookupPaths: ['invoice:discount:reason'],
            required: true,
            comment: 'Discount reason, like professional courtesy or court-mandated',
        },

        description: {
            type: 'string', comment: 'The description to include on the invoice'
        },

        /**
         * Transients
         */
        amount: {
            type: 'money', transient: true, comment: 'The total of this discount'
        },

		laborAmount: {
			type: 'money', transient: true, comment: 'Portion of the total applied to labor'
		},

		expenseAmount: {
			type: 'money', transient: true, comment: 'Portion of the total applied to expenses'
		},

    },
    indexes: [
        {name: 'notDeleted', fn: d => d.status !== 'D'}
    ],
    maps: [
        {name: 'invoiceId', keysFn: d => d.entityType === 'invoice' ? d.entityId : null, presentFn: (d, k) => d.entityType === 'invoice' && d.entityId === k && d.status !== 'D'},
        {name: 'matterId', keysFn: d => d.entityType === 'matter' ? d.entityId : null, presentFn: (d, k) => d.entityType === 'matter' && d.entityId === k && d.status !== 'D'},
        {name: 'reason', keysFn: d => d.reason, presentFn: (d, k) => d.reason === k},
    ]
});
    }
}
export default Discount;
